<script setup lang="ts">
import type { SiteBlockData, SiteBlockSettings } from '../../../types/site'

/**
 * Plan comparison. Column count adapts to how many plans the tenant entered
 * rather than being fixed, so two plans read as a pair instead of two thirds
 * of a three-up grid.
 */
const props = defineProps<{ data: SiteBlockData, settings: SiteBlockSettings }>()

const isExternal = (url?: string | null) => !!url && /^(https?:|mailto:|tel:)/.test(url)

const plans = computed(() => props.data.plans ?? [])

const gridClass = computed(() => {
  const n = plans.value.length
  if (n <= 1) return 'mx-auto max-w-md'
  if (n === 2) return 'mx-auto max-w-3xl sm:grid-cols-2'
  if (n === 4) return 'sm:grid-cols-2 lg:grid-cols-4'
  return 'sm:grid-cols-2 lg:grid-cols-3'
})
</script>

<template>
  <div>
    <SiteSectionHeading
      :eyebrow="data.eyebrow"
      :title="data.headline"
      :subtitle="data.subtext"
      class="mb-8"
    />

    <div
      v-if="plans.length"
      class="grid gap-5"
      :class="gridClass"
    >
      <div
        v-for="(plan, i) in plans"
        :key="i"
        class="site-surface-card flex flex-col p-6"
        :class="plan.highlighted ? 'ring-2 ring-[var(--site-accent)]' : ''"
      >
        <span
          v-if="plan.badge"
          class="site-bg-accent-soft site-text-accent mb-3 self-start rounded-full px-2.5 py-1 text-xs font-semibold"
        >{{ plan.badge }}</span>

        <h4 class="site-display text-lg tracking-tight">
          {{ plan.name }}
        </h4>

        <p
          v-if="plan.price"
          class="mt-2 flex items-baseline gap-1"
        >
          <span class="site-display text-3xl tracking-tight">{{ plan.price }}</span>
          <span
            v-if="plan.period"
            class="text-sm text-[var(--site-muted)]"
          >{{ plan.period }}</span>
        </p>

        <p
          v-if="plan.description"
          class="mt-3 text-sm leading-relaxed text-[var(--site-muted)]"
        >
          {{ plan.description }}
        </p>

        <ul
          v-if="plan.features?.length"
          class="mt-5 space-y-2 text-sm"
        >
          <li
            v-for="(feature, fi) in plan.features"
            :key="fi"
            class="flex items-start gap-2"
          >
            <svg
              class="site-text-accent mt-0.5 size-4 shrink-0"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              aria-hidden="true"
            >
              <path
                d="m5 13 4 4L19 7"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <span>{{ feature }}</span>
          </li>
        </ul>

        <div
          v-if="plan.button_text"
          class="mt-6 pt-2"
          :class="plan.features?.length || plan.description ? 'mt-auto' : ''"
        >
          <NuxtLink
            :to="plan.button_url || '#'"
            :external="isExternal(plan.button_url)"
            class="block w-full px-5 py-2.5 text-center text-sm font-semibold transition"
            :class="plan.highlighted ? 'site-btn-accent' : 'site-btn-outline'"
          >
            {{ plan.button_text }}
          </NuxtLink>
        </div>
      </div>
    </div>
  </div>
</template>
